import {css, html, LitElement} from 'lit';
import {customElement, property} from 'lit/decorators';
import {normalizeStyles} from '@webs/components-library/theming';
import {ifDefined} from 'lit/directives/if-defined.js';

@customElement('webs-media')
export class Media extends LitElement {
  private internalImageSrc: string;
  static styles = [
    normalizeStyles,
    css`
      .media_image-responsive img {
        display: block;
        height: auto;
        width: 100%;
      }

      .caption {
        display: flex;
        justify-content: flex-end;
        color: #A8A39F;
        font-size: 0.7rem;
        font-family: var(--font-family-alternative);
        line-height: 1.3;
        margin: 0;
        margin-top: 5px;
      }

      figure {
        margin: 0;
      }

      .video-wrapper {
        display: block;
        position: relative;
      }

      .video-wrapper:hover { cursor: pointer; }

      .video-wrapper video {
        max-height: 100%;
        width: 100%;
      }

      .video-wrapper video:not(.show) {
        left: 50%;
        position: absolute;
        top: 50%;
        transform: translate(-50%, -50%);
        z-index: -1;
      }

      .media_image-responsive { display: block; }

      .media-video video {
        position: absolute;
        width: 100%;
        height: 100%;
      }

      figure.hidden {
        display: none;
      }

      [class*="media_image"] {
        border-radius: .2rem;
        overflow: hidden;
      }

      .play-icon {
        border-radius: 50%;
        left: 50%;
        position: absolute;
        top: 50%;
        transform: translate(-50%, -50%);
      }

      .media_image-cover,
      .media_image-contain {
        display: block;
        height: 0;
        overflow: hidden;
        padding-bottom: 56.25%;
        position: relative;
      }

      .media_image-cover img,
      .media_image-contain img,
      .media_image-cover video,
      .media_image-contain video {
        display: block;
        height: 100%;
        position: absolute;
        width: 100%;
      }

      .media_image-cover img,
      .media_image-cover video {
        object-fit: cover;
      }

      .media_image-contain img,
      .media_image-contain video {
        object-fit: contain;
      }

      @media only screen and (min-width: 992px){
        .play-icon { height: 56px; width: 56px; }
      }
    `
  ];

  @property()
  href: string;

  @property({attribute: 'src'})
  get imageSrc(): string {
    return this.internalImageSrc;
  }

  set imageSrc(value: string) {
    this.internalImageSrc = value;
    this.requestUpdate('imageSrc');
  }

  @property({attribute: 'alt'})
  imageAlt: string

  @property()
  height: string

  @property({type: Number})
  width: number

  @property()
  video: string;

  @property({type: Boolean})
  autoplay: boolean

  @property()
  caption: string;

  @property({attribute: 'type'})
  objectFit = 'responsive';

  private getImage(partName: string): unknown {
    return html`
      <figure part="${partName}" class="media_image media_image-${this.objectFit}"
              style="${this.objectFit !== 'responsive' && this.height ? `padding-bottom: ${this.height}`: ''}">
        <img src="${this.imageSrc}?width=${this.width}&amp;name=${this.imageName}" alt="${this.imageAlt}" loading="lazy" width="${this.width}"
             srcset="${ifDefined(this.srcset)}" sizes="(max-width: ${this.width}px) 100vw, ${this.width}px">
      </figure>
    `;
  }

  get imageName():string{
    return this.imageSrc.split('\\').pop().split('/').pop();
  }

  get srcset(): string{
    return `
      ${this.imageSrc}?width=${this.width / 2}&amp;name=${this.imageName} ${this.width / 2}w,
      ${this.imageSrc}?width=${this.width}&amp;name=${this.imageName} ${this.width}w,
      ${this.imageSrc}?width=${this.width * 2}&amp;name=${this.imageName} ${this.width * 2}w,
      ${this.imageSrc}?width=${this.width * 2.5}&amp;name=${this.imageName} ${this.width * 2.5}w,
      ${this.imageSrc}?width=${this.width * 3}&amp;name=${this.imageName} ${this.width * 3}w`;
  }

  protected render(): unknown {
    return html`
      ${this.href ? this.getImageWithLink() : this.video ? this.getVideoPlayer() : this.getImage('media')}
      ${this.caption ? html`<p class="caption">${this.caption}</p>` : ''}
    `
  }

  getVideoPlayer(): unknown {

    return html`
      <div @click="${this.playVideo}" part="video" class="video-wrapper media_video media_image-${this.objectFit}"
           style="${this.objectFit !== 'responsive' && this.height ? `padding-bottom: ${this.height}`: ''}">
        <video autoplay="${ifDefined(this.autoplay)}" muted="" controls="" src="${this.video}"></video>
        ${!this.autoplay ? html`
          ${this.getImage('with-video')}
          <svg class="play-icon" width="40" height="40" viewBox="0 0 40 40" fill="none"
               xmlns="http://www.w3.org/2000/svg">
            <circle cx="20" cy="20" r="20" fill="#181A23" stroke="white"/>
            <path fill-rule="evenodd" clip-rule="evenodd"
                  d="M0 20C0 8.96 8.96 0 20 0C31.04 0 40 8.96 40 20C40 31.04 31.04 40 20 40C8.96 40 0 31.04 0 20ZM28 20L16 11V29L28 20Z"
                  fill="white"/>
          </svg>
        ` : ''}
      </div>
    `;
  }

  getImageWithLink(): unknown {
    return html`
      <a href="${this.href}">
        ${this.getImage('media')}
      </a>
    `;
  }

  private async playVideo(event: Event): Promise<void> {
    event.preventDefault();
    const playButton: HTMLElement = this.renderRoot.querySelector('.play-icon');
    const placeholder: HTMLElement = this.renderRoot.querySelector('.media_video figure');
    const video: HTMLVideoElement = this.renderRoot.querySelector('video');
    playButton.style.display = 'none';
    placeholder.classList.add('hidden');
    video.classList.add('show');
    await video.play();
  }
}
